import cn from 'classnames';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import userDefaultAvatar from '../../../../assets/img/placeholder-user.png';
import { ReactComponent as UserPlus } from '../../../../assets/svg/icons/user-plus.svg';
import {
    DISPLAY_REPORT_POPUP,
    MESSAGES_INFO,
    REPLY_MESSAGE,
    SET_REPORT_MESSAGE,
    SET_USER_MESSAGES_EXISTS,
} from '../../../../redux/actions/messagesActions';
import MixPanelService from '../../../../system/services/MixPanelService';

import {
    getCommonRoutePath,
    getCurrentColor,
    isIframelessVersion,
    onUserImgError,
    trackGetStarted,
} from '../../../../helpers/Helpers';
import { GET_PROSPECT_INFO } from '../../../../redux/actions/currentChatActions';
// import Auth from "../../../../components/auth/AuthCognito"; // we could move it to lazy, but carefully
import { Image } from 'antd';
import { createdAt, messageData } from '../../../../helpers/Dialogs';
import { OPEN_LOGIN_POPUP, startRegistration } from '../../../../redux/actions/accountActions';

const scrollActions = {
    block: false,
};

const MessagingBody = ({ currentChat, messages }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { account_info: accountInfo } = useSelector((state) => state.account);
    const { isMobile } = useSelector((state) => state.widget);
    const replyMessage = useSelector((state) => state.replyMessage);

    const [chat, setChat] = useState({ activeChat: null, messages: [] });

    const listenerRef = React.useRef({ messages, currentChat });

    useEffect(() => {
        listenerRef.current = { messages, currentChat };
    }, [messages, currentChat]);

    useEffect(() => {
        const element = document.getElementById('chat-body');
        if (element) {
            element.addEventListener('scroll', onBodyScroll);
        }
        checkIfUserMessagesExists();
        if (!accountInfo || !accountInfo.key) {
            handleRegister();
        }

        return () => {
            const element = document.getElementById('chat-body');
            if (element) {
                element.removeEventListener('scroll', onBodyScroll);
            }
        };
    }, []);

    useEffect(() => {
        if (
            messages.items &&
            !currentChat.userMessagesExist &&
            (currentChat.activeChat !== chat.activeChat ||
                messages.items.length !== chat.messages.length ||
                currentChat.realtimeAction)
        ) {
            setChat({ activeChat: currentChat.activeChat, messages: messages.items });
            checkIfUserMessagesExists();
        }
    }, [messages, currentChat, chat]);

    const checkIfUserMessagesExists = useCallback(() => {
        if (
            (!currentChat.userMessagesExist || currentChat.realtimeAction) &&
            messages.items.length > 1 &&
            currentChat.chatsType !== 4 &&
            accountInfo.key === 'prospect'
        ) {
            dispatch({
                type: SET_USER_MESSAGES_EXISTS,
                payload: { userMessagesExist: true },
            });
        }
    }, [dispatch, messages, currentChat]);

    const onBodyScroll = useCallback(
        (event) => {
            const { messages, currentChat } = listenerRef.current;
            const element = document.getElementById('chat-body');
            if (
                (!scrollActions.block || messages.oldLoaded) &&
                element &&
                element.scrollTop < 50 &&
                !messages.isLoading &&
                messages.items &&
                messages.items.length &&
                messages.items.length < messages.total &&
                !messages.isLastMessageLoaded &&
                !messages.isAroundMessage
            ) {
                scrollActions.block = true;
                dispatch({
                    type: MESSAGES_INFO,
                    payload: {
                        dialog_id: currentChat.data.id,
                        type_chat: currentChat.chatsType,
                        blockReloadChat: true,
                        isBlockedScroll: true,
                        loaderDisabled: true,
                        onScroll: true,
                        noResetForNewMessages: true,
                    },
                    onFinish: () => {
                        scrollActions.block = false;
                    },
                });
            } else if (
                !scrollActions.block &&
                Math.ceil(+element.scrollTop + 1) >=
                    Math.ceil(+element.scrollHeight) - Math.ceil(+element.clientHeight) &&
                !messages.isFirstMessageLoaded &&
                !messages.isAroundMessage
            ) {
                scrollActions.block = true;
                dispatch({
                    type: MESSAGES_INFO,
                    payload: {
                        dialog_id: currentChat.data.id,
                        afterMessageId: messages.items ? messages.items[messages.items.length - 1].id : null,
                        blockReloadChat: true,
                        loaderDisabled: true,
                        isBlockedScroll: true,
                        type_chat: currentChat.chatsType,
                        noResetForNewMessages: true,
                    },
                    onFinish: () => {
                        scrollActions.block = false;
                    },
                });
            }
        },
        [dispatch, messages]
    );

    const followMessage = useMemo(() => {
        const { items, activeUsers } = messages;
        const members = currentChat.data.members || [];
        if (items.length < 2 || !accountInfo || accountInfo.key !== 'prospect') return null;

        const lastMessage = items[items.length - 1];
        if (lastMessage.user_id !== accountInfo.id) return null;

        const [ambassador] = members.filter((item) => {
            return item.id !== accountInfo.id && item.role_key === 'univercity-student';
        });

        return (
            <div className="system-message" role="alert" aria-live="assertive">
                {activeUsers !== '0' &&
                    ambassador &&
                    (ambassador
                        ? 'Thanks for sending ' +
                          ambassador.name +
                          " a message, they'll get back to you as soon as they can. You’ll receive a notification to let you know when they've replied 😊"
                        : '')}
            </div>
        );
    }, [accountInfo, messages, currentChat]);

    const followGroupMessage = useMemo(() => {
        const { items } = messages;
        if (
            !accountInfo ||
            items.length < 2 ||
            !currentChat.data ||
            !currentChat.data.name ||
            accountInfo.key !== 'prospect'
        )
            return null;

        const lastMessage = items[items.length - 1];
        if (lastMessage.user_id !== accountInfo.id) return null;

        return (
            <div className="system-message" role="alert" aria-live="assertive">
                {`Thanks for getting in touch - someone from ${currentChat.data.name} will get back to you as soon as they can. You’ll receive a notification to let you know when they've replied 😊`}
            </div>
        );
    }, [accountInfo, currentChat, messages]);

    const handleRegister = useCallback(() => {
        const { data: chatData, university, chatsType } = currentChat;
        trackGetStarted(university.id, 'button', true);
        dispatch(
            startRegistration({
                university,
                id: chatData.id,
                name: chatData.name,
                type: chatsType === 4 ? 'group-chat' : 'chat',
            })
        );
    }, [dispatch, currentChat]);

    const handleLoginClick = useCallback(() => {
        dispatch({
            type: OPEN_LOGIN_POPUP,
        });
    }, [dispatch]);

    const sendReplyMessage = useCallback(
        (message) => {
            dispatch({
                type: REPLY_MESSAGE,
                payload: {
                    activeMessage: message,
                },
            });
            try {
                document.getElementById('content-messaging-textarea').focus();
            } catch (e) {}
        },
        [dispatch]
    );

    const reportMessage = useCallback(
        (message) => {
            dispatch({
                type: DISPLAY_REPORT_POPUP,
                payload: true,
            });
            dispatch({
                type: SET_REPORT_MESSAGE,
                payload: {
                    activeMessage: message,
                },
            });
        },
        [dispatch]
    );

    const goToStudent = useCallback(
        (studentId, goToProfile, userRole, publisher) => {
            if (goToProfile || !userRole) {
                onProfileClick(publisher);
                navigate(
                    `${getCommonRoutePath()}/chat-to-us/${
                        userRole && userRole === 'univercity-student' ? studentId : 'university'
                    }`
                );
            }
        },
        [navigate]
    );

    const onProfileClick = useCallback(
        (publisher) => {
            if (currentChat.chatsType === 4) {
                if (publisher && publisher.roles[0].key === 'univercity-student') {
                    MixPanelService.track('View Profile page from Group chats', {
                        Ambassador: publisher.name,
                        university_id: currentChat.university.id,
                        Prospect: accountInfo ? 'registered' : 'unregistered',
                    });
                }
            }
        },
        [accountInfo, currentChat]
    );

    const onAvatarClick = useCallback(
        (prospectId, userRole, isStudent) => {
            if (userRole === 'prospect' && isStudent) {
                dispatch({
                    type: GET_PROSPECT_INFO,
                    payload: {
                        prospectId: prospectId.id,
                    },
                });
            }
        },
        [dispatch]
    );

    const currentChatInfo = currentChat.data;
    const isStudent = accountInfo && accountInfo.key === 'univercity-student';
    const isIframeLess = isIframelessVersion();
    const commonRoutePath = getCommonRoutePath();
    let messagesRender = [];
    let currentUserColor = '#fff';

    if (messages.items) {
        const items = messages.items;
        for (const index in items) {
            const message = items[index];
            getCurrentColor(message.user, index, message.user_id);
        }
        messagesRender = items.map((message, index) => {
            if (message.type === 'text' || message.type === 'autotext') {
                if (!message.content.text) return null;

                const createdAtDate = createdAt(message);

                const text = messageData(message);

                const publisher = message.user;

                if (publisher) {
                    currentUserColor = getCurrentColor(publisher, index, message.user_id);
                }

                let parentMessagePublisher = null;
                const userDetailedInfo = '';
                const userParentMessageDetailedInfo = '';
                let parentMessageData = '';
                /* if (publisher) {
                if (publisher.user_tags && publisher.role_key === "univercity-student") {
                    const subject = publisher.user_tags.subject && publisher.user_tags.subject[0] ? publisher.user_tags.subject[0].name : "";
                    const courses = publisher.user_tags.courses_types && publisher.user_tags.courses_types[0] ? publisher.user_tags.courses_types[0].name : "";
                    userDetailedInfo = (subject && courses) ? " (" + subject + " " + courses + ")" : "";
                } else if (publisher.role_key === "prospect" && publisher.nationality && publisher.nationality.country && publisher.nationality.country.name) {
                    userDetailedInfo = " (Participant from " + publisher.nationality.country.name + ")";
                } else if (publisher.role_key === "univercity-admin" || publisher.role_key === "group-admin") {
                    userDetailedInfo = " (Administrator)";
                }
                } */
                if (message.parentMessage) {
                    parentMessagePublisher = message.parentMessage.user;
                    /*
                     if (parentMessagePublisher) {
                            if (parentMessagePublisher.user_tags && parentMessagePublisher.role_key === "univercity-student") {
                                const subjectParentPublisher = parentMessagePublisher.user_tags.subject && parentMessagePublisher.user_tags.subject[0] ? parentMessagePublisher.user_tags.subject[0].name : "";
                                const coursesParentPublisher = parentMessagePublisher.user_tags.courses_types && parentMessagePublisher.user_tags.courses_types[0] ? parentMessagePublisher.user_tags.courses_types[0].name : "";
                                userParentMessageDetailedInfo = (subjectParentPublisher && coursesParentPublisher) ? " (" + subjectParentPublisher + " " + coursesParentPublisher + ")" : "";
                            } else if (parentMessagePublisher.role_key === "prospect" && parentMessagePublisher.nationality && parentMessagePublisher.nationality.country && parentMessagePublisher.nationality.country.name) {
                                userParentMessageDetailedInfo = " (Participant from " + parentMessagePublisher.nationality.country.name + ")";
                            } else if (parentMessagePublisher.role_key === "univercity-admin" || parentMessagePublisher.role_key === "group-admin") {
                                userParentMessageDetailedInfo = " (Administrator)";
                            }
                     }
                     */
                    parentMessageData = messageData(message.parentMessage);
                }
                const userRole =
                    publisher && publisher.roles && publisher.roles[0] && publisher.roles[0].key
                        ? publisher.roles[0].key
                        : null;
                const showProfileLink =
                    !isStudent &&
                    publisher &&
                    publisher.name &&
                    userRole &&
                    userRole !== 'prospect' &&
                    userRole !== 'univercity-admin';
                return (
                    <div
                        key={message.id}
                        id={message.id}
                        className={accountInfo && accountInfo.id === message.user_id ? 'msg-item mod-my' : 'msg-item'}
                    >
                        <div className="layer" />
                        {publisher && publisher.avatar ? (
                            <div
                                className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                onClick={() => goToStudent(publisher.id, showProfileLink, userRole, publisher)}
                            >
                                <Image
                                    className="msg-item-img"
                                    src={publisher.avatar.sizes['70x70'] || publisher.avatar.original}
                                    onError={onUserImgError}
                                    alt={publisher.name ? publisher.name.slice(0, 1).toUpperCase() : 'D'}
                                />
                            </div>
                        ) : (
                            <div
                                className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                style={{ backgroundColor: currentUserColor, cursor: 'pointer' }}
                                onClick={() => onAvatarClick(publisher, userRole, isStudent)}
                            >
                                <span>
                                    {publisher && publisher.name ? publisher.name.slice(0, 1).toUpperCase() : 'D'}
                                </span>
                            </div>
                        )}
                        <div
                            className={`msg-block-item ${
                                userRole !== 'univercity-admin' &&
                                accountInfo &&
                                accountInfo.id !== message.user_id &&
                                index > 0 &&
                                publisher &&
                                !currentChatInfo.admin_closed &&
                                !currentChatInfo.closed &&
                                (!currentChatInfo.reported || currentChat.chatsType === 4) &&
                                !message.is_hidden
                                    ? 'report'
                                    : ''
                            }`}
                        >
                            {message.parentMessage && (
                                <div
                                    className={
                                        'msg-parent' +
                                        (accountInfo && accountInfo.id === message.parentMessage.user_id
                                            ? ' msg-parent-me'
                                            : '')
                                    }
                                >
                                    <p className="msg-name">
                                        {parentMessagePublisher && parentMessagePublisher.name
                                            ? parentMessagePublisher.name
                                            : 'Deleted user'}
                                        <span>{userParentMessageDetailedInfo}</span>
                                        {parentMessagePublisher && parentMessagePublisher.registered_via_group && (
                                            <span>{' from (' + parentMessagePublisher.registered_via_group + ')'}</span>
                                        )}
                                    </p>
                                    {message.parentMessage.content && message.parentMessage.content.text && (
                                        <p
                                            className="msg-text msg-text-short"
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(parentMessageData, {
                                                    ALLOWED_TAGS: [' ', 'a', 'b', 'span', 'p', 'br'],
                                                    ADD_ATTR: ['target'],
                                                }),
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                            {(!accountInfo || (accountInfo && accountInfo.id !== message.user_id)) && (
                                <p className="msg-name">
                                    {showProfileLink ? (
                                        <NavLink
                                            end
                                            to={
                                                commonRoutePath +
                                                '/chat-to-us/' +
                                                (userRole === 'univercity-student' ? publisher.id : 'university')
                                            }
                                            className={
                                                'user-name-brand' +
                                                (userRole !== 'prospect' ? ' ambassador-chat-name' : '')
                                            }
                                            onClick={() => onProfileClick()}
                                        >
                                            {publisher.name}
                                        </NavLink>
                                    ) : (
                                        <span className={!message.isUserBlocked ? 'user-name-brand' : 'user-name'}>
                                            {publisher && publisher.name ? publisher.name : 'Deleted user'}
                                        </span>
                                    )}
                                    {message.isUserBlocked && <span className="blocked-text"> (blocked)</span>}
                                    <span>{userDetailedInfo}</span>
                                    {publisher && publisher.registered_via_group && (
                                        <span>{' from (' + publisher.registered_via_group + ')'}</span>
                                    )}
                                </p>
                            )}
                            {message.is_hidden || message.isUserBlocked ? (
                                <p className="msg-text reported">Message is hidden</p>
                            ) : (
                                <p
                                    className="msg-text"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(text, {
                                            ALLOWED_TAGS: [' ', 'a', 'b', 'span', 'p', 'br'],
                                            ADD_ATTR: ['target'],
                                        }),
                                    }}
                                />
                            )}
                            <div className="msg-controls">
                                <div className="msg-controls-buttons">
                                    {accountInfo &&
                                        accountInfo.id !== message.user_id &&
                                        index > 0 &&
                                        publisher &&
                                        !currentChatInfo.admin_closed &&
                                        !currentChatInfo.closed &&
                                        (!currentChatInfo.reported || currentChat.chatsType === 4) &&
                                        !message.is_hidden &&
                                        !currentChatInfo.isMyAccountBlocked &&
                                        !message.isUserBlocked && (
                                            <>
                                                <div className="reply-button" onClick={() => sendReplyMessage(message)}>
                                                    Reply
                                                </div>
                                                {userRole !== 'univercity-admin' &&
                                                    userRole !== 'group-admin' &&
                                                    currentChat.chatsType === 4 &&
                                                    !currentChatInfo.isMyMessageReported && (
                                                        <div
                                                            className="report-button"
                                                            onClick={() => reportMessage(message)}
                                                        >
                                                            Report
                                                        </div>
                                                    )}
                                            </>
                                        )}
                                </div>
                                <div className="hidden-block">Message sent at time {createdAtDate}</div>
                                <p className="msg-time" aria-hidden="true">
                                    {createdAtDate}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            } else if (message.type === 'joined') {
                const {
                    user,
                    ambassador,
                    ambassador_id: ambassadorId,
                    ambassador_avater: ambassadorAvatar,
                    university,
                    ambassador_type: ambassadorType,
                } = message.content;

                let ambassadorAvaSmall = ambassadorAvatar;
                if (ambassadorAvaSmall) {
                    try {
                        ambassadorAvaSmall =
                            ambassadorAvatar.split('/original')[0] + '/70x70' + ambassadorAvatar.split('original')[1];
                    } catch (err) {}
                }
                currentUserColor = getCurrentColor(message.user, index, message.user_id);

                const userRole =
                    message.user && message.user.roles && message.user.roles[0] && message.user.roles[0].key
                        ? message.user.roles[0].key
                        : null;
                const showProfileLink =
                    !isStudent &&
                    message.user &&
                    message.user.id &&
                    userRole &&
                    userRole !== 'prospect' &&
                    userRole !== 'univercity-admin';
                if (!isMobile || (currentChat.chatsType !== 1 && !isStudent)) {
                    return (
                        <div key={message.id} className="join-row">
                            {message.user && message.user.avatar ? (
                                <div
                                    className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                    onClick={() => goToStudent(message.user.id, showProfileLink, userRole)}
                                >
                                    <Image
                                        className="msg-item-img"
                                        src={message.user.avatar.sizes['70x70'] || message.user.avatar.original}
                                        onError={onUserImgError}
                                        alt={message.user.name ? message.user.name.slice(0, 1).toUpperCase() : 'D'}
                                    />
                                </div>
                            ) : (
                                <div
                                    className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                    style={{ backgroundColor: currentUserColor }}
                                    onClick={() => goToStudent(message.user.id, showProfileLink, userRole)}
                                >
                                    <span>
                                        {message.user && message.user.name
                                            ? message.user.name.slice(0, 1).toUpperCase()
                                            : 'D'}
                                    </span>
                                </div>
                            )}
                            <p className="join-text">
                                {showProfileLink ? (
                                    <NavLink
                                        end
                                        to={
                                            commonRoutePath +
                                            '/chat-to-us/' +
                                            (userRole === 'univercity-student' ? message.user.id : 'university')
                                        }
                                        className="user-name-brand  ambassador-chat-name"
                                        onClick={() => onProfileClick()}
                                    >
                                        {user}
                                    </NavLink>
                                ) : (
                                    user
                                )}{' '}
                                added
                            </p>
                            <div
                                className={
                                    'msg-avatar-block' + (!isStudent && ambassadorId ? ' ambassador-chat-image' : '')
                                }
                                onClick={() =>
                                    goToStudent(ambassadorId, !!ambassadorId && !isStudent, 'univercity-student')
                                }
                            >
                                <Image
                                    className="msg-item-img"
                                    src={ambassadorAvaSmall || ambassadorAvatar || userDefaultAvatar}
                                    onError={onUserImgError}
                                    alt={ambassador ? ambassador.slice(0, 1).toUpperCase() : 'D'}
                                />
                            </div>
                            <p className="join-text">
                                {!isStudent && ambassadorId ? (
                                    <NavLink
                                        end
                                        to={commonRoutePath + '/chat-to-us/' + ambassadorId}
                                        className="user-name-brand  ambassador-chat-name"
                                        onClick={() => onProfileClick()}
                                    >
                                        {ambassador}
                                    </NavLink>
                                ) : (
                                    ambassador
                                )}
                                {ambassadorType
                                    ? ', ' + university + ' ' + ambassadorType + ', '
                                    : ' from ' + university + ' '}
                                to the chat
                            </p>
                        </div>
                    );
                } else {
                    return (
                        <div key={message.id} className="join-row">
                            <p className="join-text">
                                <UserPlus />
                                {showProfileLink ? (
                                    <NavLink
                                        end
                                        to={
                                            commonRoutePath +
                                            '/chat-to-us/' +
                                            (userRole === 'univercity-student' ? message.user.id : 'university')
                                        }
                                        className="ambassador-chat-name"
                                        onClick={() => onProfileClick()}
                                    >
                                        {user}
                                    </NavLink>
                                ) : (
                                    user
                                )}{' '}
                                added{' '}
                                {!isStudent && ambassadorId ? (
                                    <NavLink
                                        end
                                        to={commonRoutePath + '/chat-to-us/' + ambassadorId}
                                        className="user-name-brand  ambassador-chat-name"
                                        onClick={() => onProfileClick()}
                                    >
                                        {ambassador}
                                    </NavLink>
                                ) : (
                                    ambassador
                                )}
                                {ambassadorType && !isStudent && ambassadorId ? (
                                    <NavLink
                                        className="user-name-brand  ambassador-chat-name"
                                        end
                                        to={commonRoutePath + '/chat-to-us/' + ambassadorId}
                                    >
                                        , {university} {ambassadorType},{' '}
                                    </NavLink>
                                ) : (
                                    <span>, from {university}, </span>
                                )}
                                to the chat
                            </p>
                        </div>
                    );
                }
            } else if (message.type === 'joined-admin') {
                const adminName = message.content.user;
                currentUserColor = getCurrentColor(message.user, index, message.user_id);

                return (
                    <div key={message.id} className="join-row">
                        {!isStudent && message.user && message.user.avatar ? (
                            <div className="msg-avatar-block">
                                <Image
                                    className="msg-item-img"
                                    src={message.user.avatar.sizes['70x70'] || message.user.avatar.original}
                                    alt={adminName ? adminName.slice(0, 1).toUpperCase() : 'D'}
                                />
                            </div>
                        ) : (
                            <div className="msg-avatar-block" style={{ backgroundColor: currentUserColor }}>
                                <span>{adminName ? adminName.slice(0, 1).toUpperCase() : 'D'}</span>
                            </div>
                        )}
                        <p className="join-text">{adminName} joined the chat</p>
                    </div>
                );
            } else if (message.type === 'invite') {
                const {
                    user,
                    user_avatar: userAvatar,
                    admin,
                    admin_id: adminId,
                    admin_avatar: adminAvatar,
                    university,
                } = message.content;
                currentUserColor = getCurrentColor(message.user, index, message.user_id);

                let adminAvaSmall = adminAvatar;
                if (adminAvaSmall) {
                    try {
                        adminAvaSmall = adminAvatar.split('/original')[0] + '/70x70' + adminAvatar.split('original')[1];
                    } catch (err) {}
                }
                const showProfileLink = !isStudent && message.user && message.user.id;
                const userRole =
                    message.user && message.user.roles && message.user.roles[0] && message.user.roles[0].key
                        ? message.user.roles[0].key
                        : null;
                return (
                    <div key={message.id} className="join-row">
                        {message.user && message.user.avatar ? (
                            <div
                                className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                onClick={() => goToStudent(message.user.id, showProfileLink, userRole)}
                            >
                                <Image
                                    className="msg-item-img"
                                    src={userAvatar}
                                    onError={onUserImgError}
                                    alt={user.name ? user.name.slice(0, 1).toUpperCase() : 'D'}
                                />
                            </div>
                        ) : (
                            <div
                                className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                onClick={() => goToStudent(message.user.id, showProfileLink, userRole)}
                                style={{ backgroundColor: currentUserColor }}
                            >
                                <span>
                                    {message.user && message.user.name
                                        ? message.user.name.slice(0, 1).toUpperCase()
                                        : 'D'}
                                </span>
                            </div>
                        )}
                        <p className="join-text">
                            {showProfileLink ? (
                                <NavLink
                                    end
                                    to={
                                        commonRoutePath +
                                        '/chat-to-us/' +
                                        (userRole === 'univercity-student' ? message.user.id : 'university')
                                    }
                                    className="user-name-brand  ambassador-chat-name"
                                    onClick={() => onProfileClick()}
                                >
                                    {user}
                                </NavLink>
                            ) : (
                                user
                            )}{' '}
                            added
                        </p>
                        <div
                            className={'msg-avatar-block' + (!adminId ? ' ambassador-chat-image' : '')}
                            onClick={() => goToStudent(adminId, !isStudent)}
                        >
                            <Image
                                className="msg-item-img"
                                src={adminAvaSmall || adminAvatar || userDefaultAvatar}
                                onError={onUserImgError}
                                alt={admin ? admin + "'s avatar" : "Deleted User's avatar"}
                            />
                        </div>
                        <p className="join-text">
                            {!isStudent && !adminId ? (
                                <NavLink
                                    end
                                    to={commonRoutePath + '/chat-to-us/university-data'}
                                    className="user-name-brand ambassador-chat-name"
                                    onClick={() => onProfileClick()}
                                >
                                    {admin}
                                </NavLink>
                            ) : (
                                admin
                            )}
                            , {university} staff representative to the chat.
                        </p>
                    </div>
                );
            } else if (message.type === 'detached') {
                const {
                    user,
                    ambassador,
                    ambassador_id: ambassadorId,
                    ambassador_avater: ambassadorAvatar,
                } = message.content;

                let ambassadorAvaSmall = ambassadorAvatar;
                if (ambassadorAvaSmall) {
                    try {
                        ambassadorAvaSmall =
                            ambassadorAvatar.split('/original')[0] + '/70x70' + ambassadorAvatar.split('original')[1];
                    } catch (err) {}
                }
                currentUserColor = getCurrentColor(message.user, index, message.user_id);
                const showProfileLink = !isStudent && message.user && message.user.id;
                const userRole =
                    message.user && message.user.roles && message.user.roles[0] && message.user.roles[0].key
                        ? message.user.roles[0].key
                        : null;

                return (
                    <div key={message.id} className="join-row">
                        {message.user && message.user.avatar ? (
                            <div
                                className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                onClick={() => goToStudent(message.user.id, showProfileLink, userRole)}
                            >
                                <Image
                                    className="msg-item-img"
                                    src={message.user.avatar.sizes['70x70'] || message.user.avatar.original}
                                    onError={onUserImgError}
                                    alt={message.user.name ? message.user.name.slice(0, 1).toUpperCase() : 'D'}
                                />
                            </div>
                        ) : (
                            <div
                                className={'msg-avatar-block' + (showProfileLink ? ' ambassador-chat-image' : '')}
                                style={{ backgroundColor: currentUserColor }}
                                onClick={() => goToStudent(message.user.id, showProfileLink, userRole)}
                            >
                                <span>
                                    {message.user && message.user.name
                                        ? message.user.name.slice(0, 1).toUpperCase()
                                        : 'D'}
                                </span>
                            </div>
                        )}
                        <p className="join-text">
                            {showProfileLink ? (
                                <NavLink
                                    end
                                    to={commonRoutePath + '/chat-to-us/' + message.user.id}
                                    className="user-name-brand  ambassador-chat-name"
                                    onClick={() => onProfileClick()}
                                >
                                    {user}
                                </NavLink>
                            ) : (
                                user
                            )}{' '}
                            removed
                        </p>
                        <div
                            className={
                                'msg-avatar-block' + (!isStudent && ambassadorId ? ' ambassador-chat-image' : '')
                            }
                            onClick={() =>
                                goToStudent(message.user.id, !!ambassadorId && !isStudent, 'univercity-student')
                            }
                        >
                            <Image
                                className="msg-item-img"
                                src={ambassadorAvaSmall || ambassadorAvatar || userDefaultAvatar}
                                onError={onUserImgError}
                                alt={ambassador ? ambassador.slice(0, 1).toUpperCase() : 'D'}
                            />
                        </div>
                        <p className="join-text">
                            {!isStudent && ambassadorId ? (
                                <NavLink
                                    end
                                    to={commonRoutePath + '/chat-to-us/' + ambassadorId}
                                    className="user-name-brand  ambassador-chat-name"
                                    onClick={() => onProfileClick()}
                                >
                                    {ambassador}
                                </NavLink>
                            ) : (
                                ambassador
                            )}{' '}
                            from the chat
                        </p>
                    </div>
                );
            } else if (message.type === 'blocked' && currentChat.chatsType === 4) {
                return (
                    <div key={message.id} className="join-row">
                        <p className="join-text">{message.content.text}</p>
                    </div>
                );
            }
        });
    }

    return (
        <div
            className={cn('chat-body messaging-chat-body', {
                iframless: isIframeLess,
                'with-reply-active': replyMessage.activeMessage,
            })}
            id="chat-body"
            style={{ minHeight: 0 }}
        >
            <div className="chat-body-list">
                {messagesRender}
                {currentChat.chatsType === 1 ? followMessage : followGroupMessage}

                {/* {(!accountInfo || !accountInfo.key) && (
                        <>
                            <Auth
                                chatName={currentChat.data.name}
                                universityId={currentChat.university.id}
                                onRegister={handleRegister}
                                onLogin={handleLoginClick}
                                fromGroupChat={true}
                            />
                        </>
                    )} */}
            </div>
        </div>
    );
};

export default MessagingBody;
